import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  InputBase,
  Paper,
  Popper,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  IconButton,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { httpClient } from "../api/httpClient";
import { API_BASE_URL } from "../api/config";
import { fullName, initialsOf } from "../utils/format";

// Recherche globale (barre du haut) — uniquement des endpoints existants :
//  - GET /patients/search?q=…      (prénom / nom)
//  - GET /pro/search?query=…       (nom, prénom ou spécialité)
//  - GET /admin/accounts/lookup?email=…  (si la saisie est un email : tous
//    les comptes portant cet email, complets ou non)
const asList = (json) => (Array.isArray(json) ? json : Array.isArray(json?.content) ? json.content : Array.isArray(json?.data) ? json.data : []);

export const searchEverywhere = async (q) => {
  const term = q.trim();
  const tasks = [];
  if (term.includes("@")) {
    tasks.push(
      httpClient(`${API_BASE_URL}/admin/accounts/lookup?email=${encodeURIComponent(term)}`)
        .then(({ json }) =>
          (json?.accounts || []).map((a) => {
            const t = String(a.userType || a.roleType || "").toUpperCase();
            const resource = a.incomplete ? "incomplete-accounts" : t.includes("PRO") ? "professionals" : "patients";
            return {
              key: `acc-${a.id}`,
              id: a.id,
              resource,
              title: fullName(a.firstName, a.lastName, a.email),
              subtitle: `${a.email || ""} · ${a.incomplete ? "Compte incomplet" : resource === "professionals" ? "Professionnel" : "Patient"}`,
              firstName: a.firstName,
              lastName: a.lastName,
            };
          })
        )
        .catch(() => [])
    );
  } else {
    tasks.push(
      httpClient(`${API_BASE_URL}/patients/search?q=${encodeURIComponent(term)}`)
        .then(({ json }) =>
          asList(json).map((p) => ({
            key: `pat-${p.id}`,
            id: p.id,
            resource: "patients",
            title: fullName(p.firstName, p.lastName, "Patient"),
            subtitle: "Patient",
            firstName: p.firstName,
            lastName: p.lastName,
            photo: p.photoUrl,
          }))
        )
        .catch(() => [])
    );
    tasks.push(
      httpClient(`${API_BASE_URL}/pro/search?query=${encodeURIComponent(term)}`)
        .then(({ json }) =>
          asList(json).map((p) => {
            const spec = (p?.professionalCard?.specialities || [])
              .map((s) => s?.speciality?.name)
              .filter(Boolean)
              .slice(0, 2)
              .join(", ");
            return {
              key: `pro-${p.id}`,
              id: p.id,
              resource: "professionals",
              title: fullName(p.firstName, p.lastName, p.email || "Professionnel"),
              subtitle: ["Professionnel", spec || p.job, p.email].filter(Boolean).join(" · "),
              firstName: p.firstName,
              lastName: p.lastName,
              photo: p?.photo?.url,
            };
          })
        )
        .catch(() => [])
    );
  }
  const parts = await Promise.all(tasks);
  return parts.flat().slice(0, 12);
};

export const GlobalSearch = ({ compact = false }) => {
  const navigate = useNavigate();
  const anchor = React.useRef(null);
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      searchEverywhere(term).then((r) => {
        if (!alive) return;
        setResults(r);
        setActive(0);
        setLoading(false);
      });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q]);

  const go = (r) => {
    if (!r) return;
    setOpen(false);
    setQ("");
    navigate(`/${r.resource}/${encodeURIComponent(r.id)}/show`);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      go(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showPanel = open && q.trim().length >= 2;
  const [expanded, setExpanded] = React.useState(false);

  if (compact && !expanded) {
    return (
      <IconButton aria-label="Ouvrir la recherche" onClick={() => setExpanded(true)} sx={{ color: "text.secondary" }}>
        <SearchIcon />
      </IconButton>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => { setOpen(false); if (compact && !q) setExpanded(false); }}>
      <Box
        sx={
          compact
            ? { position: "absolute", left: 8, right: 8, top: 11, zIndex: 2, bgcolor: "background.paper" }
            : { position: "relative", flex: 1, maxWidth: 440, ml: 2 }
        }
      >
        <Box
          ref={anchor}
          sx={(t) => ({
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            height: 38,
            borderRadius: 2.5,
            bgcolor: t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#F1F4F1",
            border: "1px solid transparent",
            transition: "all .15s",
            "&:focus-within": {
              bgcolor: t.palette.background.paper,
              borderColor: alpha(t.palette.primary.main, 0.5),
              boxShadow: `0 0 0 3px ${alpha(t.palette.primary.main, 0.12)}`,
            },
          })}
        >
          <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <InputBase
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            autoFocus={compact}
            onKeyDown={onKeyDown}
            placeholder="Rechercher un patient, un pro, un email…"
            inputProps={{ "aria-label": "Recherche globale" }}
            sx={{ flex: 1, fontSize: 14 }}
          />
          {loading && <CircularProgress size={16} />}
          {compact && (
            <IconButton size="small" aria-label="Fermer la recherche" onClick={() => { setQ(""); setExpanded(false); }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Popper open={showPanel} anchorEl={anchor.current} placement="bottom-start" sx={{ zIndex: 1400 }} style={{ width: anchor.current?.offsetWidth }}>
          <Paper sx={{ mt: 0.75, border: 1, borderColor: "divider", boxShadow: "0 12px 32px rgba(13,26,13,0.12)", overflow: "hidden" }}>
            {results.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {loading ? "Recherche…" : "Aucun résultat. Astuce : saisissez un email complet pour retrouver tout compte."}
              </Typography>
            ) : (
              <List dense sx={{ py: 0.5, maxHeight: 420, overflow: "auto" }}>
                {results.map((r, i) => (
                  <ListItemButton key={r.key} selected={i === active} onMouseEnter={() => setActive(i)} onClick={() => go(r)} sx={{ mx: 0.5, borderRadius: 2 }}>
                    <ListItemAvatar sx={{ minWidth: 42 }}>
                      <Avatar src={r.photo || undefined} sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700, bgcolor: r.resource === "professionals" ? "primary.main" : "info.main" }}>
                        {initialsOf(r.firstName, r.lastName)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={r.title}
                      secondary={r.subtitle}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                      secondaryTypographyProps={{ fontSize: 12 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
